import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Users, Clock, CheckCircle2, XCircle, UserX } from "lucide-react";

export default function CompanyAccessManagement() {
  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);

  const load = async () => {
    try {
      const [p, a] = await Promise.all([api.get("/access-requests/pending"), api.get("/access-requests/approved")]);
      setPending(p.data); setApproved(a.data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore caricamento"); }
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try { await api.post(`/access-requests/${id}/approve`); toast.success("Accesso approvato"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };
  const reject = async (id) => {
    try { await api.post(`/access-requests/${id}/reject`); toast.success("Richiesta rifiutata"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };
  const revoke = async (id) => {
    try { await api.post(`/access-requests/${id}/revoke`); toast.success("Accesso revocato"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="p-6 md:p-10 max-w-5xl">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-blue-600 mb-2">Accessi Consulenti</div>
        <h1 className="font-display font-black text-4xl md:text-5xl">Gestione Consulenti</h1>
        <p className="text-slate-500 mt-2 text-sm max-w-xl">Approva o rifiuta le richieste dei consulenti che vogliono accedere alla tua azienda. Puoi revocare l'accesso in qualsiasi momento.</p>
      </div>

      <div className="mb-8">
        <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><Clock className="h-5 w-5 text-amber-600" /> Richieste in attesa ({pending.length})</h2>
        {pending.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-8 text-center text-slate-500 text-sm">Nessuna richiesta in sospeso.</div>
        ) : (
          <div className="space-y-3">
            {pending.map((r) => (
              <div key={r.id} data-testid={`pending-request-${r.id}`} className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-slate-900">{r.consultant_name}</div>
                  <div className="text-xs text-slate-600 truncate">{r.consultant_email}</div>
                  <div className="text-[11px] text-slate-500 mt-1">Richiesta il {r.created_at?.slice(0, 10)}</div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => reject(r.id)} data-testid={`reject-${r.id}`} className="h-9 text-red-700 hover:bg-red-50 border-red-300">
                    <XCircle className="h-4 w-4" /> Rifiuta
                  </Button>
                  <Button size="sm" onClick={() => approve(r.id)} data-testid={`approve-${r.id}`} className="h-9 bg-emerald-600 hover:bg-emerald-700">
                    <CheckCircle2 className="h-4 w-4" /> Approva
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><Users className="h-5 w-5 text-emerald-600" /> Consulenti autorizzati ({approved.length})</h2>
        {approved.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-8 text-center text-slate-500 text-sm">Nessun consulente collegato.</div>
        ) : (
          <div className="space-y-2">
            {approved.map((r) => (
              <div key={r.id} data-testid={`approved-${r.id}`} className="bg-white border border-slate-200 rounded-lg p-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-slate-900">{r.consultant_name}</div>
                  <div className="text-xs text-slate-600 truncate">{r.consultant_email}</div>
                  <div className="text-[11px] text-emerald-700 mt-1">Approvato il {r.approved_at?.slice(0, 10)}</div>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="outline" data-testid={`revoke-${r.id}`} className="h-9 text-red-700 hover:bg-red-50 border-red-300">
                      <UserX className="h-4 w-4" /> Revoca
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Revocare l'accesso a {r.consultant_name}?</AlertDialogTitle>
                      <AlertDialogDescription>Il consulente non potrà più vedere audit, aziende e report della tua organizzazione.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annulla</AlertDialogCancel>
                      <AlertDialogAction onClick={() => revoke(r.id)} className="bg-red-600 hover:bg-red-700">Revoca</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
